import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ExternalLink, ImagePlus, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, coachHeaders } from "../lib/api";
import { AideBloc, aideImageUrl } from "./AideContent";

const TYPES = [["titre", "Titre d'étape"], ["soustitre", "Sous-titre"], ["texte", "Paragraphe"], ["encadre", "Encadré"], ["image", "Image"]];
const input = "w-full rounded-lg border border-line bg-ink px-3 py-2 text-sm text-chalk outline-none focus:border-lime";
const iconBtn = "rounded-lg border border-line p-1.5 text-chalk transition-colors hover:border-lime hover:text-lime disabled:opacity-30";

// Réduit l'image (max 1280 px de large) pour qu'elle reste légère à charger sur téléphone.
const compresser = (file) => new Promise((ok, ko) => {
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, 1280 / img.width);
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * k);
    c.height = Math.round(img.height * k);
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(img.src);
    ok(c.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = ko;
  img.src = URL.createObjectURL(file);
});

const ImageField = ({ b, i, code, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await api.post("/aide/images", { data: await compresser(file) }, coachHeaders(code));
      onChange({ image: data.id });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi de l'image impossible");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      {b.image ? <img src={aideImageUrl(b.image)} alt="" className="max-h-48 rounded-lg border border-line" /> : <p className="text-sm text-dim">Aucune image{b.nom ? ` (prévue : ${b.nom})` : ""}</p>}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} className="hidden" data-testid={`aide-edit-file-${i}`} />
      <button type="button" data-testid={`aide-edit-upload-${i}`} onClick={() => ref.current?.click()} disabled={busy}
        className="inline-flex items-center gap-2 rounded-full border border-lime px-4 py-1.5 text-sm text-lime hover:bg-lime hover:text-ink disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}{b.image ? "Remplacer l'image" : "Choisir une image"}
      </button>
      <input data-testid={`aide-edit-legende-${i}`} value={b.texte} maxLength={300} onChange={(e) => onChange({ texte: e.target.value })} placeholder="Légende (facultatif)" className={input} />
    </div>
  );
};

const BlocEditor = ({ b, i, n, code, onChange, onMove, onDelete, onAdd }) => (
  <li data-testid={`aide-edit-bloc-${i}`} className="rounded-2xl border border-line bg-ink/40 p-3">
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-xs text-dim">{i + 1}</span>
      <select data-testid={`aide-edit-type-${i}`} value={b.type} onChange={(e) => onChange({ type: e.target.value })} className="rounded-lg border border-line bg-ink px-2 py-1 text-sm text-chalk">
        {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <div className="ml-auto flex gap-1">
        <button type="button" aria-label="Monter" data-testid={`aide-edit-up-${i}`} disabled={i === 0} onClick={() => onMove(-1)} className={iconBtn}><ArrowUp className="h-4 w-4" /></button>
        <button type="button" aria-label="Descendre" data-testid={`aide-edit-down-${i}`} disabled={i === n - 1} onClick={() => onMove(1)} className={iconBtn}><ArrowDown className="h-4 w-4" /></button>
        <button type="button" aria-label="Ajouter un bloc dessous" data-testid={`aide-edit-add-${i}`} onClick={onAdd} className={iconBtn}><Plus className="h-4 w-4" /></button>
        <button type="button" aria-label="Supprimer" data-testid={`aide-edit-delete-${i}`} onClick={onDelete} className={`${iconBtn} hover:border-signal hover:text-signal`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
    <div className="mt-2">
      {b.type === "image"
        ? <ImageField b={b} i={i} code={code} onChange={onChange} />
        : <textarea data-testid={`aide-edit-texte-${i}`} value={b.texte} maxLength={3000} rows={b.type === "titre" || b.type === "soustitre" ? 1 : 4} onChange={(e) => onChange({ texte: e.target.value })} className={`${input} resize-y`} />}
    </div>
  </li>
);

export const AideEditor = ({ code }) => {
  const [aide, setAide] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/aide").then((r) => setAide({ titre: r.data?.titre ?? "", blocs: Array.isArray(r.data?.blocs) ? r.data.blocs : [] })).catch(() => toast.error("Chargement de la page d'aide impossible"));
  }, []);
  if (!aide) return <p className="text-dim">Chargement…</p>;

  const setBlocs = (f) => setAide((a) => ({ ...a, blocs: f(a.blocs) }));
  const nouveau = { type: "texte", texte: "", image: "", nom: "" };
  const move = (i, d) => setBlocs((bs) => { const c = [...bs]; [c[i], c[i + d]] = [c[i + d], c[i]]; return c; });
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/aide", aide, coachHeaders(code));
      toast.success("Page d'aide enregistrée");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Enregistrement impossible");
    } finally { setSaving(false); }
  };
  const manquantes = aide.blocs.filter((b) => b.type === "image" && !b.image).length;

  return (
    <div data-testid="aide-editor" className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-2xl border border-line bg-pitch p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-2xl uppercase text-chalk">Page d'aide</h2>
          <a href="/aide" target="_blank" rel="noreferrer" data-testid="aide-edit-open" className="inline-flex items-center gap-1 text-sm text-lime hover:underline"><ExternalLink className="h-4 w-4" />Voir la page</a>
        </div>
        <p className="mt-1 text-sm text-dim">Écris **comme ceci** pour mettre un mot en gras. Les blocs « Image » sans image ne sont pas affichés aux parents.</p>
        {manquantes > 0 && <p data-testid="aide-edit-missing" className="mt-2 rounded-lg border border-signal/50 bg-signal/10 px-3 py-2 text-sm text-chalk">{manquantes} image{manquantes > 1 ? "s" : ""} à ajouter.</p>}
        <label className="mt-3 block"><span className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-dim">Titre de la page</span>
          <input data-testid="aide-edit-titre" value={aide.titre} maxLength={200} onChange={(e) => setAide((a) => ({ ...a, titre: e.target.value }))} className={input} />
        </label>
        <ol className="mt-3 space-y-2">
          {aide.blocs.map((b, i) => (
            <BlocEditor key={i} b={b} i={i} n={aide.blocs.length} code={code}
              onChange={(p) => setBlocs((bs) => bs.map((x, k) => (k === i ? { ...x, ...p } : x)))}
              onMove={(d) => move(i, d)}
              onDelete={() => setBlocs((bs) => bs.filter((_, k) => k !== i))}
              onAdd={() => setBlocs((bs) => [...bs.slice(0, i + 1), nouveau, ...bs.slice(i + 1)])} />
          ))}
        </ol>
        <div className="sticky bottom-3 mt-3 flex gap-2">
          <button type="button" data-testid="aide-edit-add-end" onClick={() => setBlocs((bs) => [...bs, nouveau])} className="inline-flex items-center gap-1 rounded-full border border-line bg-pitch px-4 py-2 text-sm text-chalk hover:border-lime"><Plus className="h-4 w-4" />Bloc</button>
          <button type="button" data-testid="aide-edit-save" onClick={save} disabled={saving} className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-lime px-5 py-2 font-semibold text-ink disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Enregistrer
          </button>
        </div>
      </section>
      <section data-testid="aide-preview" className="hidden rounded-2xl border border-line bg-ink p-4 lg:block">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-lime">Aperçu</p>
        <h1 className="mt-2 font-display text-3xl uppercase leading-none text-chalk">{aide.titre}</h1>
        {aide.blocs.map((b, i) => <AideBloc key={i} b={b} i={`preview-${i}`} editor />)}
      </section>
    </div>
  );
};
